/**
 * Google Pay's pay.js calls `Object.defineProperties(Array.prototype, { md: <getter-only> })`,
 * polluting Array.prototype. Chakra's responsive helper then does `Object.assign(array, { md })`
 * on every theme recompute, which throws because `md` is a getter with no setter — looping the
 * render until the tab OOM-crashes (seen on the US/CA order-confirmation page after Google Pay
 * loads on the payment step). This guard rewrites such getter-only breakpoint-key definitions on
 * Array.prototype to a harmless writable property. Must run before Google Pay loads.
 */
const BREAKPOINT_KEYS = new Set(["base", "sm", "md", "lg", "xl", "2xl", "3xl", "4xl", "5xl"]);

const harmlessDescriptor = (): PropertyDescriptor => ({
    value: undefined,
    writable: true,
    configurable: true,
    enumerable: false,
});

const isPollutingAccessor = (prop: PropertyKey, descriptor?: PropertyDescriptor): boolean =>
    typeof prop === "string" &&
    BREAKPOINT_KEYS.has(prop) &&
    !!descriptor &&
    typeof descriptor.get === "function" &&
    typeof descriptor.set !== "function";

export const installArrayPrototypePollutionGuard = (): void => {
    if (typeof window === "undefined") return;

    const guardedWindow = window as Window & { __arrayProtoGuardInstalled?: boolean };
    if (guardedWindow.__arrayProtoGuardInstalled) return;
    guardedWindow.__arrayProtoGuardInstalled = true;

    const proto = Array.prototype;
    const originalDefineProperty = Object.defineProperty;
    const originalDefineProperties = Object.defineProperties;

    Object.defineProperty = function defineProperty(target, property, descriptor) {
        if (target === proto && isPollutingAccessor(property, descriptor)) {
            return originalDefineProperty(target, property, harmlessDescriptor());
        }
        return originalDefineProperty(target, property, descriptor);
    } as typeof Object.defineProperty;

    Object.defineProperties = function defineProperties(target, descriptors) {
        if (target === proto && descriptors) {
            let safeDescriptors = descriptors;
            for (const key of Object.keys(descriptors)) {
                const descriptor = (descriptors as PropertyDescriptorMap)[key];
                if (isPollutingAccessor(key, descriptor)) {
                    if (safeDescriptors === descriptors) {
                        safeDescriptors = { ...descriptors };
                    }
                    (safeDescriptors as PropertyDescriptorMap)[key] = harmlessDescriptor();
                }
            }
            return originalDefineProperties(target, safeDescriptors);
        }
        return originalDefineProperties(target, descriptors);
    } as typeof Object.defineProperties;
};
